import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Typography, View } from '@shinetools/sunshine-universal';
import { AnimatePresence, motion } from 'framer-motion';

import asBentoPage from 'common/bento/hoc/asBentoPage';
import Button from 'components/_core/Button';
import * as Form from 'components/_core/form';
import SunshineCard from 'components/_core/SunshineCard';
import OBNavigation from 'features/Onboarding/components/OBNavigation/OBNavigation';

import Header from '../../../../components/Header';
import CapitalDepositQuestionPage from '../../components/QuestionPage';
import partnerNameSchema, {
  PartnerNameSchema,
} from '../../libs/partnerNameSchema';
import { CapitalDepositPage } from '../../types';
import locales from './locales';

const OPTIONS = [
  {
    label: 'Keobiz',
    value: 'Keobiz',
  },
  {
    label: 'LegalPlace',
    value: 'LegalPlace',
  },
  {
    label: 'Legalstart',
    value: 'Legalstart',
  },
  {
    label: 'Comptastar',
    value: 'Comptastar',
  },
  {
    label: 'Captain Contrat',
    value: 'Captain Contrat',
  },
  {
    label: 'Indy',
    value: 'Indy',
  },
  {
    label: 'Dougs',
    value: 'Dougs',
  },
  {
    label: 'Gojee',
    value: 'Gojee',
  },
  {
    label: 'Agence-Juridique.com',
    value: 'Agence-Juridique.com',
  },
  {
    label: 'Contract Factory',
    value: 'Contract Factory',
  },
  {
    label: 'Les Tricolores',
    value: 'Les Tricolores',
  },
  {
    label: locales.partnerOptions.lawyer,
    requestDetails: true,
    value: 'Un avocat',
  },
  {
    label: locales.partnerOptions.accountingFirm,
    requestDetails: true,
    value: 'Un cabinet d’expertise comptable',
  },
  {
    label: locales.partnerOptions.other,
    requestDetails: true,
    value: 'Autre',
  },
] as const;

export type CapitalDepositPartnerValue = (typeof OPTIONS)[number]['value'];

type CapitalDepositPartnerNameProps = {
  onSubmit: (values: PartnerNameSchema) => void;
  formData: Partial<PartnerNameSchema>;
  isSending: boolean;
} & CapitalDepositPage;

const CapitalDepositPartnerName = asBentoPage<CapitalDepositPartnerNameProps>(
  ({ formData, isSending, Layout, onPrev, onSubmit, progress }) => {
    const {
      control,
      formState: { isValid },
      handleSubmit,
      setValue,
    } = useForm<PartnerNameSchema>({
      defaultValues: formData,
      mode: 'onChange',
      resolver: zodResolver(partnerNameSchema),
    });

    return (
      <Layout nav={<OBNavigation onPrev={onPrev} />}>
        <CapitalDepositQuestionPage
          as="form"
          button={
            <Button
              icon="arrow-right"
              isDisabled={!isValid}
              isLoading={isSending}
              type="submit"
            >
              {locales.ctaSubmit}
            </Button>
          }
          onSubmit={handleSubmit(onSubmit)}
          progress={progress}
        >
          <Header title={locales.title} />

          <Controller
            control={control}
            name="partnerName"
            render={({ field: { onChange, value } }) => (
              <SunshineCard.RadioGroup
                onChange={(nextValue) => {
                  setValue('partnerDetails', null);
                  onChange(nextValue);
                }}
                paddingBottom="space-24"
                value={value}
              >
                {OPTIONS.map((option) => (
                  <SunshineCard
                    key={option.value}
                    title={option.label}
                    value={option.value}
                    variant="radio"
                  >
                    <AnimatePresence>
                      {'requestDetails' in option &&
                      option.requestDetails &&
                      option.value === value ? (
                        <Controller
                          control={control}
                          name="partnerDetails"
                          render={({
                            field: {
                              onChange: onDetailsChange,
                              value: detailsValue,
                            },
                          }) => (
                            <SunshineCard.Content
                              animate={{
                                height: 'auto',
                                opacity: 1,
                                transition: {
                                  ease: 'easeInOut',
                                },
                              }}
                              as={motion.div}
                              exit={{ height: 0, opacity: 0 }}
                              initial={{ height: 0, opacity: 0 }}
                              overflow="hidden"
                            >
                              <View paddingTop="$space.16" width="100%">
                                <Typography.Text
                                  marginBottom="$space.4"
                                  size="small"
                                >
                                  {locales.detailsLabel}
                                </Typography.Text>
                                <Form.Input
                                  name="partnerDetails"
                                  onChange={onDetailsChange}
                                  value={detailsValue ?? ''}
                                  width="full"
                                />
                              </View>
                            </SunshineCard.Content>
                          )}
                        />
                      ) : null}
                    </AnimatePresence>
                  </SunshineCard>
                ))}
              </SunshineCard.RadioGroup>
            )}
          />
        </CapitalDepositQuestionPage>
      </Layout>
    );
  },
);

export default CapitalDepositPartnerName;
